import React, { Fragment, useContext, useEffect, useState } from "react";
import { Box, TextField, Button, Grid, Typography, Checkbox } from "@mui/material";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import AuthLayout from "../common/AuthLayout/AuthLayout";
import { useMutation } from "@apollo/client/react";
import get from "lodash/get";
import changeEmailMutation from "./ChangeEmailMutation";
import { ChangeEmailFormValues } from "./EmailValidation";
import authService from "../../services/authService";
import AppContext from "../../context/AppContext";
import styles from "./ChangeEmailPage.module.css";
import { GraphQLError } from "graphql";
import { has } from "lodash";
import { ChangeEmail, ChangeEmailVariables } from "./types/ChangeEmail";
import { SkipUserMfa, SkipUserMfaVariables } from "../MFAEnrolment/types/SkipUserMfa";
import SkipUserMfaMutation from "../MFAEnrolment/SkipUserMfa";
import MobileNumberField from "../common/InputField/MobileNumberField";
import Tooltips from "../common/Tooltip/Tooltip";
import InvalidateMutation from "./InvalidateMutation";
import * as yup from "yup";
import { isValidOptionalMobileNumber, MOBILE_NUMBER_FORMAT_MESSAGE } from "../../utils/mobile";
import { invalidateImmediately } from "./types/invalidateImmediately";
import Divider from "@mui/material/Divider";
import { CombinedGraphQLErrors, ServerError } from "@apollo/client/errors";
import StandardizedHeader from "../GeneralHeader/StandardizedHeader";

type ChangeEmailBoxType = {
  onSubmit: (
    newEmail: string,
    newMobileNumber: string,
    autoRefreshResult: boolean,
    previewMode: boolean,
    openNewTab: boolean,
    resetMfa: boolean,
    hidePanel: boolean,
    autoSearch: boolean,
    discoverable: boolean,
  ) => Promise<void>;
  error?: any;
  loading?: boolean;
  submitSuccess: boolean;
};

export const ChangeEmailBox: React.FC<ChangeEmailBoxType> = (props) => {
  const { error, loading, submitSuccess } = props;
  const navigate = useNavigate();
  const initialValues: ChangeEmailFormValues = {
    newEmail: "",
    message: "",
    resultNotification: 1,
    notificationEmail: "",
    mobile: "",
  };

  const isDesktop = useContext(AppContext).isDesktop;
  const isEnforced = authService.getHomepageEnforced();
  const [primaryEmail, setPrimaryEmail] = useState(authService.getPrimaryEmail());
  const [mobileNumber, setMobileNumber] = useState(authService.getMobileNumber());
  const { autoRefreshResult, previewMode } = useContext(AppContext);
  const isMobility = authService.isMobility();
  const [toggleAutoRefreshResult, setToggleAutoRefreshResult] = useState(autoRefreshResult);
  const [togglePreviewMode, setTogglePreviewMode] = useState(previewMode);
  const [toggleOpenNewTabMode, setToggleOpenNewTabMode] = useState(authService.getOpenNewTab());
  const [toggleHidePanel, setToggleHidePanel] = useState(authService.getHidePanel());
  const [toggleAutoSearch, setToggleAutoSearch] = useState(authService.getAutoSearch());
  const [toggleDiscoverable, setDiscoverable] = useState(authService.getDiscoverable());
  const [resetMfa, setResetMfa] = useState(false);
  const [invalidateSuccess, setInvalidateSuccess] = useState("");
  const [invalidateError, setInvalidateError] = useState("");

  const formValidationSchema = yup.object().shape(
    {
      newEmail: yup.string().email("Enter a valid email"),
      mobile: yup
        .string()
        // Shared canonical mobile validation (see utils/mobile.ts). Optional field:
        // empty passes, otherwise must be a valid AU mobile (10 digits, "04" prefix).
        // The field displays 0400 000 000 but emits/stores digits only.
        .test("Mobile Test", MOBILE_NUMBER_FORMAT_MESSAGE, isValidOptionalMobileNumber),
    },
    [["newEmail", "mobile"]],
  );

  useEffect(() => {
    if (!authService.isAuthenticated) {
      navigate(`/contact-support`);
    }
  }, [navigate]);

  const [SkipUserMfa] = useMutation<SkipUserMfa, SkipUserMfaVariables>(SkipUserMfaMutation);

  const [InvalidateSessions] = useMutation<invalidateImmediately>(InvalidateMutation);

  const resetUserMFA = async () => {
    await SkipUserMfa({
      variables: {
        reset: true,
      },
    });
  };

  const submit = () => {
    if (!authService.isCognito() && resetMfa == true) {
      resetUserMFA();
    }
    props.onSubmit(
      primaryEmail,
      mobileNumber,
      toggleAutoRefreshResult,
      togglePreviewMode,
      toggleOpenNewTabMode,
      !authService.isCognito() ? resetMfa : false,
      toggleHidePanel,
      toggleAutoSearch,
      toggleDiscoverable,
    );
  };

  const invalidateUser = async () => {
    await InvalidateSessions().then((response) => {
      if (response.data?.invalidateImmediately.message.toLowerCase().includes("success")) {
        setInvalidateSuccess(response.data.invalidateImmediately.message);
      } else {
        setInvalidateError(response.data?.invalidateImmediately.message || "");
      }
    });
  };

  const handleDeleteAccountClick = () => {
    navigate("/delete-account");
  };

  return (
    <Grid container className={isDesktop ? styles.mainBox : styles.mainBoxM}>
      <Grid size={{ xs: 12 }}>
        <AuthLayout title="" hideHeading maxWidth={"690px"}>
          <Box>
            <Formik
              validationSchema={formValidationSchema}
              enableReinitialize={false}
              initialValues={initialValues}
              onSubmit={submit}
            >
              {({ setFieldValue, errors, handleBlur, handleSubmit, touched }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <Box>
                      <Typography color="primary" className={styles.subHeading}>
                        Change your primary email
                      </Typography>
                    </Box>
                    <span>
                      <Box>
                        {error && (() => {
                          if (CombinedGraphQLErrors.is(error)) {
                            return error.errors.map((graphqlError, index) => (
                              <Typography key={index} color="error">
                                {graphqlError.message}
                              </Typography>
                            ));
                          } else if (ServerError.is(error)) {
                            return <Typography color="error">{error.message}</Typography>;
                          } else {
                            return <Typography color="error">{(error as any)?.message || 'An error occurred'}</Typography>;
                          }
                        })()}
                      </Box>
                      <Box mt={4}>
                        <TextField
                          id="newEmail"
                          name="newEmail"
                          fullWidth
                          label="Primary Email"
                          type="email"
                          error={!!errors.newEmail && touched.newEmail}
                          variant="outlined"
                          onChange={(value: any) => {
                            const pemail = value.target.value.trim();
                            setFieldValue("newEmail", pemail);
                            setPrimaryEmail(pemail);
                          }}
                          onBlur={handleBlur}
                          value={primaryEmail}
                          required={true}
                          helperText={touched.newEmail && errors.newEmail}
                          disabled={authService.getSharedAccount()}
                        ></TextField>
                      </Box>

                      <Box style={{ marginTop: "20px" }}>
                        <Divider />
                      </Box>
                    </span>
                    <span>
                      <Box>
                        <Typography color="primary" className={styles.subHeading}>
                          Change your mobile number
                        </Typography>
                      </Box>

                      <Box mt={4}>
                        <MobileNumberField
                          initState={mobileNumber}
                          helperText={touched.mobile && errors.mobile}
                          inputChange={(value: any) => {
                            setFieldValue("mobile", value);
                            setMobileNumber(value.replace(/\s/g, ""));
                          }}
                          error={!!errors.mobile && touched.mobile}
                          disabled={authService.getSharedAccount()}
                        />
                      </Box>
                      {(!isMobility && isDesktop) || !isEnforced ? (
                        <>
                          <Box style={{ marginTop: "20px" }}>
                            <Divider />
                          </Box>
                          <Box>
                            <Typography color="primary" className={styles.subHeading}>
                              Home page settings
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        ""
                      )}
                    </span>
                    <span>
                      {!isMobility && isDesktop && (
                        <>
                          <Box data-testid="setting-auto-refresh">
                            <Checkbox
                              checked={toggleAutoRefreshResult}
                              name="autoRefresh"
                              color="primary"
                              onChange={(_, checked) => {
                                setToggleAutoRefreshResult(checked);
                              }}
                            />
                            Auto Refresh Results every 15 minutes
                          </Box>
                          <Box data-testid="setting-preview-mode">
                            <Checkbox
                              checked={togglePreviewMode}
                              name="previewMode"
                              color="primary"
                              onChange={(_, checked) => {
                                setTogglePreviewMode(checked);
                              }}
                            />
                            Enable Preview Mode <Tooltips text="PreviewMode" />
                          </Box>
                          <Box data-testid="setting-open-new-tab">
                            <Checkbox
                              checked={toggleOpenNewTabMode}
                              name="previewMode"
                              color="primary"
                              onChange={(_, checked) => {
                                setToggleOpenNewTabMode(checked);
                              }}
                            />
                            Automatically Open Results in New Tab (Requires Logout) <Tooltips text="NewTab" />
                          </Box>
                        </>
                      )}
                      {!isEnforced && (
                        <>
                          <Box data-testid="setting-hide-panel">
                            <Checkbox
                              checked={toggleHidePanel}
                              name="hidePanel"
                              color="primary"
                              onChange={(_, checked) => {
                                setToggleHidePanel(checked);
                              }}
                            />
                            Hide Panel Name in Results
                          </Box>
                          <Box data-testid="setting-display-patients">
                            <Checkbox
                              checked={toggleAutoSearch}
                              name="autoSearch"
                              color="primary"
                              onChange={(_, checked) => {
                                setToggleAutoSearch(checked);
                              }}
                            />
                            Display Patients on Home Screen
                          </Box>
                        </>
                      )}
                    </span>
                    {!authService.isCognito() && authService.getForceAuth() == false && authService.getOnlySkipMFAenrolled() == true && (
                      <>
                        <Box>
                          <Typography color="primary" className={styles.subHeading}>
                            Multi Factor Authentication <Tooltips text="EnrollMFA" />
                          </Typography>
                        </Box>
                        <Box>
                          <Checkbox
                            name="resetMfa"
                            color="primary"
                            onChange={(_, checked) => {
                              setResetMfa(checked);
                            }}
                          />
                          Enrol in MFA at next logon (Requires Logout to Re-enrol)
                        </Box>
                      </>
                    )}
                    <Box>
                      <Divider />
                      <Typography color="primary" className={styles.subHeading}>
                        Share settings
                      </Typography>
                      <Box data-testid="setting-discoverable">
                        <Checkbox
                          name="discoverable"
                          color="primary"
                          checked={toggleDiscoverable}
                          onChange={(_, checked) => {
                            setDiscoverable(checked);
                          }}
                          disabled={authService.getSharedAccount()}
                        />
                        Make your account discoverable by other eResults users{" "}
                        <Tooltips text="By turning off this function, other users will not be able to share results with you through eResults. Not applicable to Shared account." />
                      </Box>
                    </Box>
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      {!authService.getDelegatedUser() ? (
                        <Button
                          type="submit"
                          color="primary"
                          variant="contained"
                          disabled={loading || authService.getSharedAccount()}
                          onClick={handleDeleteAccountClick}
                          className={styles.deleteAccountButton}
                        >
                          Delete Account
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          color="primary"
                          variant="contained"
                          disabled={loading}
                          onClick={invalidateUser}
                          className={styles.deleteAccountButton}
                        >
                          Log out all sessions
                        </Button>
                      )}
                      {submitSuccess && <Box className={styles.successMessage}>Successfully saved your changes.</Box>}
                      {!submitSuccess && invalidateSuccess !== "" && (
                        <Box className={styles.successMessage}>{invalidateSuccess}</Box>
                      )}
                      {!submitSuccess && invalidateError !== "" && (
                        <Box className={styles.errorMessage}>{invalidateError}</Box>
                      )}
                      <Button type="submit" color="primary" variant="contained" disabled={loading}>
                        Save
                      </Button>
                    </Box>
                  </form>
                );
              }}
            </Formik>
          </Box>
        </AuthLayout>
      </Grid>
    </Grid>
  );
};

const ChangeEmailPage: React.FC = () => {
  const [success, setSuccess] = useState(false);
  const [changeEmail, { loading, error }] = useMutation<ChangeEmail, ChangeEmailVariables>(changeEmailMutation);
  const { setAutoRefreshResult, setPreviewMode } = useContext(AppContext);

  const onSubmit =
    (
      changeEmail: useMutation.ResultTuple<ChangeEmail, ChangeEmailVariables>[0],
    ) =>
    async (
      newEmail: string,
      newMobileNumber: string,
      autoRefreshResult: boolean,
      previewMode: boolean,
      openNewTab: boolean,
      resetMfa: boolean,
      hidePanel: boolean,
      autoSearch: boolean,
      discoverable: boolean,
    ) => {
      try {
        const currentEmail = localStorage.getItem("primary-email");
        const currentMobileNumber = localStorage.getItem("mobile-number");
        const currentHidePanel = authService.getHidePanel();
        const currentAutoSearch = authService.getAutoSearch();
        const currentDiscoverable = authService.getDiscoverable();
        setSuccess(false);

        if (
          currentEmail !== newEmail ||
          currentMobileNumber !== newMobileNumber ||
          currentHidePanel !== hidePanel ||
          currentAutoSearch !== autoSearch ||
          currentDiscoverable !== discoverable
        ) {
          const result = await changeEmail({
            variables: {
              input: {
                newEmail,
                resetMfa,
                newMobileNumber,
                hidePanel,
                autoSearch,
                discoverable,
              },
            },
          });

          authService.setMobileNumber(get(result, "data.changeEmail.mobileNumber", ""));
          authService.setDiscoverable(discoverable);

          if (get(result, "data.changeEmail.email")) {
            authService.setPrimaryEmail(get(result, "data.changeEmail.email", ""));
            setSuccess(true);
          }

          if (has(result, "data.changeEmail.notificationEmails")) {
            authService.setNotificationEmail(get(result, "data.changeEmail.notificationEmails", "") as string);
            setSuccess(true);
          }

          if (get(result, "data.changeEmail.resultNotification")) {
            authService.setResultNotification(get(result, "data.changeEmail.resultNotification", "") as string);
          }
        }

        localStorage.setItem("AutoRefreshResult", autoRefreshResult.toString());
        localStorage.setItem("PreviewMode", previewMode.toString());
        localStorage.setItem("OpenNewTab", openNewTab.toString());
        localStorage.setItem("hidePanel", hidePanel.toString());
        localStorage.setItem("autoSearch", autoSearch.toString());
        setAutoRefreshResult(autoRefreshResult);
        setPreviewMode(previewMode);
        setSuccess(true);

        // On save settings, post message (notify) PatientListPage.tsx
        // Ref: http://jira.pl.pathlabs.com.au/browse/ER7-63
        window.opener.postMessage("reloadPage", "*");
      } catch (e) {
        if (
          e.graphQLErrors &&
          (e.graphQLErrors as GraphQLError[]).find((m) => m.message === "Cannot Change Email - Shared Account") !==
            undefined
        ) {
          authService.setSharedAccount(true);

          // allow saving of account settings for shared account
          localStorage.setItem("AutoRefreshResult", autoRefreshResult.toString());
          localStorage.setItem("PreviewMode", previewMode.toString());
          localStorage.setItem("OpenNewTab", openNewTab.toString());
          setAutoRefreshResult(autoRefreshResult);
          setPreviewMode(previewMode);
          // setOpenNewTab(openNewTab)
          setSuccess(true);

          // On save settings, post message (notify) PatientListPage.tsx
          // Ref: http://jira.pl.pathlabs.com.au/browse/ER7-63
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage("reloadPage", "*");
          }
        }

        if (
          e.graphQLErrors &&
          (e.graphQLErrors as GraphQLError[]).find((m) => m.message === "Email already exists") !== undefined
        ) {
          setSuccess(false);
        }

        setTimeout(function () {
          setSuccess(false);
        }, 3000);
      }
    };
  return (
    <Fragment>
      <StandardizedHeader title={"Account Settings"} />
      <ChangeEmailBox
        onSubmit={onSubmit(changeEmail)}
        error={error}
        loading={loading}
        submitSuccess={success}
      />
    </Fragment>
  );
};

export default ChangeEmailPage;
